import React from 'react';
import { X, ListPlus, PencilIcon } from 'lucide-react';

interface TaskModalsProps {
  editingNotes: string | null;
  notesDraft: string;
  addingSubtaskFor: string | null;
  newSubtaskTitle: string;
  onCloseNotes: () => void;
  onSaveNotes: () => void;
  onUpdateNotesDraft: (value: string) => void;
  onCloseSubtask: () => void;
  onSaveSubtask: (e: React.FormEvent) => void;
  onUpdateSubtaskTitle: (value: string) => void;
}

const MAX_NOTE_LENGTH = 2000;
const MAX_SUBTASK_LENGTH = 200;

export function TaskModals({
  editingNotes,
  notesDraft,
  addingSubtaskFor,
  newSubtaskTitle,
  onCloseNotes,
  onSaveNotes,
  onUpdateNotesDraft,
  onCloseSubtask,
  onSaveSubtask,
  onUpdateSubtaskTitle
}: TaskModalsProps) {
  return (
    <>
      {editingNotes && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] overflow-hidden">
            <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mt-3 sm:hidden" />
            <div className="flex items-center justify-between p-4 sm:p-4 border-b dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Notes pour la tache
              </h2>
              <button
                onClick={onCloseNotes}
                className="p-2 sm:p-1 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-full transition-colors touch-manipulation"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div className="p-4 sm:p-4">
              <div className="mb-2 flex justify-between items-center">
                <label className="text-sm text-gray-600 dark:text-gray-400">
                  Note detaillee
                </label>
                <span className={`text-xs ${
                  notesDraft.length > MAX_NOTE_LENGTH * 0.9
                    ? 'text-red-500 dark:text-red-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {notesDraft.length}/{MAX_NOTE_LENGTH}
                </span>
              </div>
              <textarea
                value={notesDraft}
                onChange={(e) => {
                  if (e.target.value.length <= MAX_NOTE_LENGTH) {
                    onUpdateNotesDraft(e.target.value);
                  }
                }}
                className="w-full px-3.5 py-3 sm:px-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-xl sm:rounded-lg focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm"
                rows={6}
                placeholder="Ajoutez vos notes ici..."
                maxLength={MAX_NOTE_LENGTH}
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={onCloseNotes}
                  className="flex-1 sm:flex-none px-4 py-3 sm:py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-xl sm:rounded-lg transition-colors touch-manipulation"
                >
                  Annuler
                </button>
                <button
                  onClick={onSaveNotes}
                  className="flex-1 sm:flex-none px-4 py-3 sm:py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 active:bg-primary-800 rounded-xl sm:rounded-lg transition-colors touch-manipulation"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {addingSubtaskFor && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md">
            <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mt-3 sm:hidden" />
            <div className="flex items-center justify-between p-4 sm:p-4 border-b dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Ajouter une sous-tache
              </h2>
              <button
                onClick={onCloseSubtask}
                className="p-2 sm:p-1 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-full transition-colors touch-manipulation"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <form
              className="p-4 sm:p-4 pb-6 sm:pb-4"
              onSubmit={onSaveSubtask}
            >
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1.5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Contenu de la sous-tache
                  </label>
                  <span className={`text-xs ${
                    newSubtaskTitle.length > MAX_SUBTASK_LENGTH * 0.9
                      ? 'text-red-500 dark:text-red-400'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {newSubtaskTitle.length}/{MAX_SUBTASK_LENGTH}
                  </span>
                </div>
                <input
                  type="text"
                  value={newSubtaskTitle}
                  onChange={(e) => {
                    if (e.target.value.length <= MAX_SUBTASK_LENGTH) {
                      onUpdateSubtaskTitle(e.target.value);
                    }
                  }}
                  className="w-full px-3.5 py-3 sm:px-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-xl sm:rounded-lg focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm"
                  placeholder="Entrez le contenu de la sous-tache..."
                  maxLength={MAX_SUBTASK_LENGTH}
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onCloseSubtask}
                  className="flex-1 sm:flex-none px-4 py-3 sm:py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-xl sm:rounded-lg transition-colors touch-manipulation"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 sm:flex-none px-4 py-3 sm:py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 active:bg-primary-800 rounded-xl sm:rounded-lg transition-colors touch-manipulation disabled:opacity-50"
                  disabled={!newSubtaskTitle.trim()}
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}